import { CONTACT_CONSTANTS } from "@/features/survey/components/coverSection/constants";
import useSurveyState from "@hooks/useSurveyState";
import {
  useAddSurveyContactMutation,
  useDeleteSurveyContactMutation,
  useReorderSurveyContactMutation,
  useUpdateSurveyContactMutation,
} from "@services/survey-contacts";
import type { SurveyContact } from "@survey-types/survey.contacts.types";
import {
  addContact,
  setContactEditingId,
  updateContact,
  updateCurrentSurvey,
} from "@survey/surveySlice";
import { useRef } from "react";

/**
 * Hook for managing all contact-related actions (CRUD operations)
 */
export const useContactActions = () => {
  const { survey, dispatch } = useSurveyState();
  const isProcessingSelection = useRef(false);

  const [addSurveyContact, { isLoading: isAdding }] =
    useAddSurveyContactMutation();
  const [deleteSurveyContact, { isLoading: isDeleting }] =
    useDeleteSurveyContactMutation();
  const [updateSurveyContact, { isLoading: isUpdating }] =
    useUpdateSurveyContactMutation();
  const [reorderSurveyContact] = useReorderSurveyContactMutation();

  const handleSelectContact = async (selected: unknown) => {
    if (!selected || typeof selected !== "object") return;

    if (isProcessingSelection.current) return;
    isProcessingSelection.current = true;

    const contact = selected as {
      value: string;
      name?: string;
      email?: string;
      title?: string;
      phone?: string;
    };

    if (contact.value === "no-results") {
      isProcessingSelection.current = false;
      return;
    }

    try {
      await addSurveyContact({
        name: contact.name ?? "",
        email: contact.email ?? contact.value,
        title: contact.title ?? "",
        phoneNumber: contact.phone ?? "",
        surveyId: survey?.id ?? "",
      });
    } catch (_error) {
      console.error("Error adding contact");
    } finally {
      setTimeout(() => {
        isProcessingSelection.current = false;
      }, CONTACT_CONSTANTS.FORM.SELECTION_DELAY_MS);
    }
  };

  const handleCreateContact = async (values: {
    name: string;
    title: string;
    email: string;
    phoneNumber: string;
    licenseNo: string;
  }) => {
    if (!survey?.id) return;

    const tempContact: SurveyContact = {
      contactId: `temp-${Date.now()}`,
      surveyId: survey.id,
      ...values,
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
      createdBy: "",
      updatedBy: "",
      order: survey.contacts?.length ?? 0,
    };

    dispatch(addContact(tempContact));

    try {
      await addSurveyContact({ ...values, surveyId: survey.id });
    } catch (error) {
      console.error("Error creating contact");
      throw error;
    }
  };

  const handleUpdateContact = async (
    contactId: string,
    updates: Partial<SurveyContact>,
  ) => {
    dispatch(updateContact({ contactId, updates }));

    try {
      const result = await updateSurveyContact({ contactId, contact: updates });
      if (!("error" in result)) {
        dispatch(setContactEditingId(null));
      }
    } catch (error) {
      console.error("Error updating contact");
      throw error;
    }
  };

  const handleRemoveContact = async (contactId: string) => {
    try {
      await deleteSurveyContact(contactId);
    } catch (error) {
      console.error("Error removing contact");
      throw error;
    }
  };

  const handleReorderContacts = async (
    reorderedContacts: readonly SurveyContact[],
    _oldIndex: number,
    newIndex: number,
  ) => {
    if (!survey?.id) return;

    dispatch(
      updateCurrentSurvey({
        contacts: reorderedContacts.map((contact, index) => ({
          ...contact,
          order: index,
        })),
      }),
    );

    try {
      await reorderSurveyContact({
        surveyId: survey.id,
        contactId: reorderedContacts[newIndex].contactId,
        order: newIndex,
      }).unwrap();
    } catch (_error) {
      console.error("Failed to reorder contact");
    }
  };

  return {
    handleSelectContact,
    handleCreateContact,
    handleUpdateContact,
    handleRemoveContact,
    handleReorderContacts,
    isProcessingSelection,
    isAdding,
    isUpdating,
    isDeleting,
  };
};
